<template>
  <v-form ref="form">
    <v-card class="mx-auto my-10" max-width="640">
      <v-card-title>
        <h2>Data Exports</h2>
      </v-card-title>
      <v-card-text>
        <v-autocomplete
          v-model="selectedCampaign"
          :items="campaigns"
          label="Campaigns"
          multiple
          chips
          prepend-icon="mdi-domain"
        ></v-autocomplete>
        <v-autocomplete
          v-model="selectedDashboarder"
          :items="dashboarderIds"
          label="Dashboarder Ids"
          multiple
          chips
          prepend-icon="mdi-account"
        ></v-autocomplete>
        <v-autocomplete
          v-model="selectedRejectReason"
          :items="rejectReasons"
          label="Reject Reasons"
          multiple
          chips
          prepend-icon="mdi-book-cancel"
        ></v-autocomplete>
        <v-autocomplete
          v-model="selectedRetailer"
          :items="retailers"
          item-text="name"
          item-value="id"
          label="Retailers"
          multiple
          chips
          prepend-icon="mdi-store"
        ></v-autocomplete>
        <div class="d-flex">
          <date-picker required icon="mdi-calendar" label="Start Date" v-model="startDate" writable></date-picker>
          <date-picker required icon="mdi-calendar" label="End Date" v-model="endDate" writable></date-picker>
        </div>
      </v-card-text>
      <v-card-actions>
        <v-btn color="green darken-1" :loading="isLoading" text @click="_retrieveData">
          <v-icon>mdi-database-arrow-down</v-icon>Retrieve Data
        </v-btn>
        <v-btn color="blue darken-1" :loading="isLoading" text @click="_downloadData">
          <v-icon>mdi-download</v-icon>Download Data
        </v-btn>
      </v-card-actions>
    </v-card>
    <v-card v-if="items && items.length" class="mx-auto" max-width="1000">
      <v-card-title>
        <h2>Result</h2>
      </v-card-title>
      <v-card-text>
        <v-data-table
        :headers="headers"
          :items="items"
          :items-per-page="itemsPerPage"
          :page.sync="page"
          :server-items-length="items.length"
          @update:items-per-page="updateItemsPerPage"
          @page-count="updatePageNumber"
        >
          <template v-slot:item.createdAt="{ item }">
            {{ formatDate(item.createdAt) }}
          </template>
          <template v-slot:item.updatedAt="{ item }">
            {{ formatDate(item.updatedAt) }}
          </template>
          <template v-slot:item.receiptDateTime="{ item }">
            {{ formatDate(item.receiptDateTime) }}
          </template>
          <!-- Pagination controls -->
          <template v-slot:footer="{ props }">
            <v-pagination v-model="page" :length="totalPages" @input="updatePageNumber" />
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-form>
</template>
<script>
import DatePicker from "../components/DatePicker.vue";
import { mapActions, mapGetters } from "vuex";
import moment from "moment";

export default {
  data() {
    return {
      campaigns: ['all'],
      rejectReasons: ['all'],
      dashboarderIds: ['all'],
      retailers: ['all'],
      selectedCampaign: "",
      selectedDashboarder: "",
      selectedRejectReason: "",
      selectedRetailer: "",
      startDate: "",
      endDate: "",
      isLoading: false,
      headers: [
        { text: 'Dash. ID', value: 'dashboarderId' },
        { text: 'Reference Number', value: 'referenceNumber' },
        { text: 'Created At', value: 'createdAt' },
        { text: 'Updated At', value: 'updatedAt' },
        { text: 'Status', value: 'status' },
        { text: 'Cashback', value: 'cashback' },
        { text: 'Reject Reason', value: 'rejectReason' },
        { text: 'Receipt Date/Time', value: 'receiptDateTime' },
        { text: 'Receipt ID', value: 'receiptId' },
        { text: 'Retailer', value: 'retailer' },
        { text: 'Payment Status', value: 'paymentStatus' },
        { text: 'Consumer ID', value: 'consumerId' },
        { text: 'IP Address', value: 'ipAddress' },
      ],
      items: [],
      page: 1, // Current page
      itemsPerPage: 10, // Items per page
      totalPages: 1, // Total pages
    };
  },
  components: {
    DatePicker
  },
  computed: {
    ...mapGetters(["selectedDashboard"]),
  },
  methods: {
    ...mapActions(["snackAlert", "getUserInputs", "retrieveData", "downloadData"]),
    async _retrieveData () {
      if (!Date.parse(this.startDate) || !Date.parse(this.endDate)) {
        this.snackAlert({
          message: "Please select start and end date",
          color: "error",
        });
        return;
      }

      const data = {
        campaigns: !this.selectedCampaign ? "all" : this.selectedCampaign.includes("all") ? "all" : this.selectedCampaign,
        dashboarderIds: !this.selectedDashboarder ? "all" : this.selectedDashboarder.includes("all") ? "all" : this.selectedDashboarder,
        rejectReasons: !this.selectedRejectReason ? "all" : this.selectedRejectReason.includes("all") ? "all" : this.selectedRejectReason,
        retailers: !this.selectedRetailer ? "all" : this.selectedRetailer.includes("all") ? "all" : this.selectedRetailer,
        date: `${this.formatDate(this.startDate)}-${this.formatDate(this.endDate)}`,
        page: this.page,
        count: this.itemsPerPage == -1 ? 50 : this.itemsPerPage,
      };

      try {
        this.isLoading = true;
        const response = await this.retrieveData({ data, dashboard: this.selectedDashboard });
        this.items = response.data;
        this.totalPages = response.totalPages;

        if (!this.items.length) {
          this.snackAlert({
            message: "No data found",
            color: "error",
          });
        }
        this.isLoading = false;
      } catch (error) {
        console.error("Failed to fetch data", error);
        this.isLoading = false;
        this.snackAlert({
          message: "Failed to fetch data",
          color: "error",
        });
      }
    },
    async _downloadData () {
      if (!Date.parse(this.startDate) || !Date.parse(this.endDate)) {
        this.snackAlert({
          message: "Please select start and end date",
          color: "error",
        });
        return;
      }

      const data = {
        campaigns: !this.selectedCampaign ? "all" : this.selectedCampaign.includes("all") ? "all" : this.selectedCampaign,
        dashboarderIds: !this.selectedDashboarder ? "all" : this.selectedDashboarder.includes("all") ? "all" : this.selectedDashboarder,
        rejectReasons: !this.selectedRejectReason ? "all" : this.selectedRejectReason.includes("all") ? "all" : this.selectedRejectReason,
        retailers: !this.selectedRetailer ? "all" : this.selectedRetailer.includes("all") ? "all" : this.selectedRetailer,
        date: `${this.formatDate(this.startDate)}-${this.formatDate(this.endDate)}`,
      };

      try {
        this.isLoading = true;
        const response = await this.downloadData({ data, dashboard: this.selectedDashboard });
        // Parse to see if there is an error
        if (response.data.includes("error")) {
          this.isLoading = false;
          this.snackAlert({
            message: "The data seems to be empty",
            color: "error",
          });
          return;
        }
        this.downloadCSV(response.data);
        this.isLoading = false;
      } catch (error) {
        console.error("Failed to download data", error);
        this.isLoading = false;
        this.snackAlert({
          message: "Failed to download data",
          color: "error",
        });
      }
    },
    downloadCSV(csvData) {
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'data.csv';
      link.click();
    },
    formatDate(date) {
      const temp = new Date(date);
      return moment(temp).format("YYYY/MM/DD");
    },
    updateItemsPerPage(newItemsPerPage) {
      if (this.itemsPerPage === newItemsPerPage) return;
      this.itemsPerPage = newItemsPerPage;
      this._retrieveData();
    },
    updatePageNumber(page) {
      if (page == 0) return;
      this._retrieveData();
    },
  },
  async mounted() {
    try {
      this.isLoading = true;
      const response = await this.getUserInputs({ dashboard: this.selectedDashboard });
      this.campaigns.push(...response.campaigns);
      this.rejectReasons.push(...response.rejectReasons);
      this.dashboarderIds.push(...response.dashboarderIds);
      this.retailers.push(...response.retailers);

      this.isLoading = false;
    } catch (error) {
      console.error("Failed to fetch user inputs data", error);
      this.isLoading = false;
      this.snackAlert({
        message: "Failed to fetch data",
        color: "error",
      });
    }
  }
};
</script>